﻿import { formatReceiptMoney, formatReceiptWhen, escapeReceiptHtml, receiptPlainTextCols } from './format';
import { encodeEscPosChars, ESC_POS_CUT, ESC_POS_PROLOGUE } from './escposCharset';
import { escposQrBlock } from './escposQr';
import {
  pickupTrackingQrHtml,
  pickupTrackingQrLabel,
  pickupTrackingUrl,
} from './receiptPickupTracking';
import {
  receiptCenterLineOnPaper,
  receiptColsForEscPos,
  receiptJustifyLine,
  receiptLeftLine,
  receiptPlainTextLines,
  receiptRightAlignLine,
  receiptSeparatorLine,
} from './receiptPlainLayout';
import { thermalItemRowsHtml, wrapThermalHtml } from './renderThermalHtml';
import type { FiscalReceiptDocument, FiscalReceiptPayload } from './fiscalTypes';
import type { ReceiptWidthMm } from './types';

/**
 * Marcador de posición del QR dentro de la secuencia de líneas.
 * En `text` se omite; en `escpos` emite el bloque `GS ( k`; en `html` es el `<img>`.
 */
const QR_LINE = { kind: 'qr_afip' } as const;
const PICKUP_QR_LINE = { kind: 'qr_pickup' } as const;
type FiscalAlignLine = { align: 'left' | 'center'; text: string };
type FiscalDocLine = FiscalAlignLine | null | typeof QR_LINE | typeof PICKUP_QR_LINE;

/** Unifica copy de consumidor final en el comprobante impreso. */
export function normalizeFiscalBuyerName(name?: string | null): string {
  const t = name?.trim();
  if (!t || t.toLowerCase() === 'consumidor final') return 'Consumidor Final';
  return t;
}

/** Desglose neto/IVA solo en comprobantes RI con IVA real (Factura A o IVA > 0). */
export function fiscalDiscriminatesIva(
  payload: FiscalReceiptPayload,
  net: number | null,
  iva: number | null,
): boolean {
  if (iva != null && Math.abs(iva) > 0.001) return true;
  const label = payload.documentTypeLabel.toUpperCase();
  const isFacturaA =
    label.includes('FACTURA A') ||
    label.includes('NOTA DE CRÉDITO A') ||
    label.includes('NOTA DE CREDITO A');
  return isFacturaA && net != null && net > 0;
}

function fiscalIvaIncludedLegend(emitterTaxCondition: string | null): string {
  if (emitterTaxCondition?.toLowerCase().includes('monotributo')) {
    return 'IVA incluido (Monotributo)';
  }
  return 'IVA incluido en el total';
}

function fiscalLinePlain(line: FiscalAlignLine, cols: number): string {
  return line.align === 'center' ? centered(line.text, cols) : line.text;
}

function isFiscalAlignLine(line: FiscalDocLine): line is FiscalAlignLine {
  return line != null && typeof line === 'object' && 'text' in line;
}

function pushLeft(out: FiscalDocLine[], text: string, cols: number): void {
  const t = text.trim();
  if (!t) return;
  out.push({ align: 'left', text: t.slice(0, cols) });
}

function pushLeftWrapped(out: FiscalDocLine[], text: string, cols: number): void {
  for (const line of receiptPlainTextLines(text, cols)) {
    pushLeft(out, line, cols);
  }
}

function pushCenterWrapped(out: FiscalDocLine[], text: string, cols: number): void {
  for (const line of receiptPlainTextLines(text, cols)) {
    out.push({ align: 'center', text: line });
  }
}

function encoder(): { bytes: number[]; push: (...vals: number[]) => void; text: (s: string) => void } {
  const bytes: number[] = [];
  return {
    bytes,
    push(...vals) {
      bytes.push(...vals);
    },
    text(s: string) {
      bytes.push(...encodeEscPosChars(s));
    },
  };
}

function trimOrNull(v?: string | null): string | null {
  const t = v?.trim();
  return t ? t : null;
}

function centered(s: string, width: number): string {
  const line = s.slice(0, width);
  const pad = Math.max(0, Math.floor((width - line.length) / 2));
  return `${' '.repeat(pad)}${line}`;
}

function centeredWrapped(s: string, cols: number): string[] {
  return receiptPlainTextLines(s, cols).map((line) => centered(line, cols));
}

function fiscalTotalLine(total: string, cols: number): string {
  const label = 'TOTAL';
  const amount = total.slice(0, cols);
  const gap = cols - label.length - amount.length;
  if (gap >= 2) return `${label}${'.'.repeat(gap)}${amount}`;
  return `${label} ${amount}`;
}

/** `00003-00000042` — PV padStart 5, número padStart 8. */
export function formatComprobanteNumber(pointOfSale: number, invoiceNumber: number): string {
  const pv = String(Math.max(0, Math.trunc(pointOfSale))).padStart(5, '0');
  const nro = String(Math.max(0, Math.trunc(invoiceNumber))).padStart(8, '0');
  return `${pv}-${nro}`;
}

/** `XX-XXXXXXXX-X` a partir de 11 dígitos; si no son 11, devuelve el input trimmeado. */
export function formatFiscalCuit(cuit: string): string {
  const digits = cuit.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }
  return cuit.trim();
}

/** dd/mm/aaaa — fecha de calendario, sin shift de timezone (inicio de actividades DATE). */
function formatFiscalDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

type FiscalView = {
  docTypeLabel: string;
  emitterName: string;
  emitterCuit: string | null;
  emitterTaxCondition: string | null;
  emitterAddress: string | null;
  emitterIibb: string | null;
  activityStart: string | null;
  pvNro: string;
  issuedWhen: string | null;
  buyerName: string;
  buyerCuit: string | null;
  buyerTaxCondition: string | null;
  salePaymentCondition: string | null;
  discriminatesIva: boolean;
  netAmount: string | null;
  ivaAmount: string | null;
  total: string;
  cae: string;
  caeExpiration: string | null;
  qrUrl: string | null;
  qrImageDataUrl: string | null;
  saleRef: string | null;
  copyLabel: string | null;
  relatedDocLabel: string | null;
  isHomologacion: boolean;
};

function fiscalView(payload: FiscalReceiptPayload): FiscalView {
  const net = payload.netAmount != null ? payload.netAmount : null;
  const iva = payload.ivaAmount != null ? payload.ivaAmount : null;
  const issuedWhen = formatReceiptWhen(payload.issuedAt) || null;
  return {
    docTypeLabel: payload.documentTypeLabel.trim(),
    emitterName: payload.emitterBusinessName.trim(),
    emitterCuit: trimOrNull(payload.emitterCuit)
      ? formatFiscalCuit(payload.emitterCuit)
      : null,
    emitterTaxCondition: trimOrNull(payload.emitterTaxConditionLabel),
    emitterAddress: trimOrNull(payload.emitterAddress),
    emitterIibb: trimOrNull(payload.emitterIibb),
    activityStart: formatFiscalDate(payload.emitterActivityStart) || null,
    pvNro: formatComprobanteNumber(payload.pointOfSale, payload.invoiceNumber),
    issuedWhen,
    buyerName: normalizeFiscalBuyerName(payload.buyerName),
    buyerCuit: trimOrNull(payload.buyerCuit) ? formatFiscalCuit(payload.buyerCuit!) : null,
    buyerTaxCondition: trimOrNull(payload.buyerTaxConditionLabel),
    salePaymentCondition: trimOrNull(payload.salePaymentConditionLabel),
    discriminatesIva: fiscalDiscriminatesIva(payload, net, iva),
    netAmount: net != null ? formatReceiptMoney(net) : null,
    ivaAmount: iva != null ? formatReceiptMoney(iva) : null,
    total: formatReceiptMoney(payload.total),
    cae: payload.cae.trim(),
    caeExpiration: formatFiscalDate(payload.caeExpiration) || null,
    qrUrl: trimOrNull(payload.qrUrl),
    qrImageDataUrl: trimOrNull(payload.qrImageDataUrl),
    saleRef: trimOrNull(payload.saleRef),
    copyLabel: trimOrNull(payload.copyLabel),
    relatedDocLabel: trimOrNull(payload.relatedDocLabel),
    isHomologacion: payload.environment === 'homologacion',
  };
}

function fiscalItemDescription(index: number, line: FiscalReceiptPayload['lines'][number]): string {
  const qtyPart = line.qty > 1 ? `${line.qty}x ` : '';
  return `${index + 1}. ${qtyPart}${line.name}`;
}

function fiscalItemLines(payload: FiscalReceiptPayload, cols: number): FiscalAlignLine[] {
  const out: FiscalAlignLine[] = [];
  payload.lines.forEach((line, index) => {
    const left = fiscalItemDescription(index, line);
    const right = formatReceiptMoney(line.lineTotal);
    const one = receiptJustifyLine(left, right, cols);
    if (one) {
      pushLeft(out, one, cols);
      return;
    }
    for (const row of receiptPlainTextLines(left, cols)) pushLeft(out, row, cols);
    pushLeft(out, receiptRightAlignLine(right, cols), cols);
  });
  return out;
}

function showBuyerTaxCondition(view: FiscalView): boolean {
  const cond = view.buyerTaxCondition?.trim();
  if (!cond) return false;
  if (cond === 'Consumidor Final' && view.buyerName === 'Consumidor Final') return false;
  return true;
}

function fiscalDocLines(payload: FiscalReceiptPayload, view: FiscalView, cols: number): FiscalDocLine[] {
  const sep = receiptSeparatorLine(cols);
  const out: FiscalDocLine[] = [];

  pushLeftWrapped(out, view.emitterName, cols);
  if (view.emitterAddress) pushLeftWrapped(out, view.emitterAddress, cols);
  if (view.emitterCuit) pushLeft(out, `CUIT: ${view.emitterCuit}`, cols);
  if (view.emitterTaxCondition) {
    pushLeftWrapped(out, `Condición IVA: ${view.emitterTaxCondition}`, cols);
  }
  if (view.emitterIibb) pushLeft(out, `IIBB: ${view.emitterIibb}`, cols);
  if (view.activityStart) {
    pushLeftWrapped(out, `Inicio de actividades: ${view.activityStart}`, cols);
  }

  pushCenterWrapped(out, view.docTypeLabel, cols);
  if (view.copyLabel) pushCenterWrapped(out, view.copyLabel, cols);

  pushLeft(out, sep, cols);
  const [pvCode, compNro] = view.pvNro.includes('-')
    ? view.pvNro.split('-', 2)
    : [view.pvNro, ''];
  pushLeft(out, `Pto. vta.: ${pvCode}`, cols);
  if (compNro) pushLeft(out, `Nro. comprob.: ${compNro}`, cols);
  if (view.issuedWhen) pushLeft(out, `Fecha de emisión: ${view.issuedWhen}`, cols);
  pushLeftWrapped(out, `Razón social: ${view.buyerName}`, cols);
  if (view.buyerCuit) pushLeft(out, `CUIT: ${view.buyerCuit}`, cols);
  if (showBuyerTaxCondition(view)) {
    pushLeftWrapped(out, `Condición IVA: ${view.buyerTaxCondition}`, cols);
  }
  if (view.salePaymentCondition) pushLeft(out, `Condición de venta: ${view.salePaymentCondition}`, cols);

  pushLeft(out, sep, cols);
  for (const row of fiscalItemLines(payload, cols)) out.push(row);
  pushLeft(out, sep, cols);

  if (view.discriminatesIva && view.netAmount != null) {
    pushLeft(out, `Neto gravado ${view.netAmount}`, cols);
  }
  if (view.discriminatesIva && view.ivaAmount != null) {
    pushLeft(out, `IVA ${view.ivaAmount}`, cols);
  }
  if (!view.discriminatesIva) {
    pushLeftWrapped(out, fiscalIvaIncludedLegend(view.emitterTaxCondition), cols);
  }
  pushLeft(out, fiscalTotalLine(view.total, cols), cols);

  pushLeft(out, sep, cols);
  if (view.relatedDocLabel) pushCenterWrapped(out, view.relatedDocLabel, cols);
  if (view.qrUrl) {
    pushCenterWrapped(out, 'Escanee el código para verificar en AFIP', cols);
    out.push(QR_LINE);
  }
  pushCenterWrapped(out, `C.A.E. N° ${view.cae}`, cols);
  if (view.caeExpiration) pushCenterWrapped(out, `Fecha de vto. CAE: ${view.caeExpiration}`, cols);
  pushCenterWrapped(out, 'Comprobante autorizado por AFIP', cols);
  if (view.saleRef) pushLeftWrapped(out, `Referencia interna: ${view.saleRef}`, cols);
  if (view.isHomologacion) {
    pushCenterWrapped(out, 'COMPROBANTE DE PRUEBA - SIN VALIDEZ FISCAL', cols);
  }

  const tracking = pickupTrackingUrl(payload.trackingUrl);
  if (tracking) {
    pushLeft(out, sep, cols);
    pushCenterWrapped(out, pickupTrackingQrLabel(payload.fulfillmentLabel), cols);
    out.push(PICKUP_QR_LINE);
  }

  return out;
}

function fiscalCols(widthMm: ReceiptWidthMm, escpos: boolean): number {
  return escpos ? receiptColsForEscPos(widthMm) : receiptPlainTextCols(widthMm);
}

/** ESC/POS del comprobante fiscal (CP850; QR nativo si viene `qrUrl`). */
export function renderFiscalComprobanteEscPos(payload: FiscalReceiptPayload): Uint8Array {
  const widthMm = payload.widthMm ?? 72;
  const cols = fiscalCols(widthMm, true);
  const view = fiscalView(payload);
  const out = encoder();
  out.push(...ESC_POS_PROLOGUE);
  const tracking = pickupTrackingUrl(payload.trackingUrl);
  for (const line of fiscalDocLines(payload, view, cols)) {
    if (line == null) continue;
    if (line === QR_LINE) {
      out.push(...escposQrBlock(view.qrUrl!));
      continue;
    }
    if (line === PICKUP_QR_LINE) {
      if (tracking) out.push(...escposQrBlock(tracking));
      continue;
    }
    if (!isFiscalAlignLine(line)) continue;
    const plain =
      line.align === 'center'
        ? receiptCenterLineOnPaper(line.text, cols, cols)
        : receiptLeftLine(line.text, cols);
    out.text(`${plain}\n`);
  }
  out.push(...ESC_POS_CUT);
  return Uint8Array.from(out.bytes);
}

export function buildFiscalComprobante(payload: FiscalReceiptPayload): FiscalReceiptDocument {
  const widthMm = payload.widthMm ?? 72;
  const view = fiscalView(payload);
  const cols = fiscalCols(widthMm, false);
  const docLines = fiscalDocLines(payload, view, cols);

  const text = docLines
    .filter(
      (l): l is FiscalAlignLine => l != null && l !== QR_LINE && l !== PICKUP_QR_LINE,
    )
    .map((l) => fiscalLinePlain(l, cols))
    .join('\n');

  const meta = (s: string, cls = 'meta') =>
    `<div class="${cls}">${escapeReceiptHtml(s)}</div>`;

  const emitterHtml = [
    meta(view.emitterName),
    ...(view.emitterAddress ? receiptPlainTextLines(view.emitterAddress, cols).map((l) => meta(l)) : []),
    view.emitterCuit ? meta(`CUIT: ${view.emitterCuit}`) : '',
    view.emitterTaxCondition ? meta(`Condición IVA: ${view.emitterTaxCondition}`) : '',
    view.emitterIibb ? meta(`IIBB: ${view.emitterIibb}`) : '',
    view.activityStart ? meta(`Inicio de actividades: ${view.activityStart}`) : '',
  ].join('');

  const [pvCode, compNro] = view.pvNro.includes('-')
    ? view.pvNro.split('-', 2)
    : [view.pvNro, ''];

  const buyerHtml = [
    meta(`Pto. vta.: ${pvCode}`),
    compNro ? meta(`Nro. comprob.: ${compNro}`) : '',
    view.issuedWhen ? meta(`Fecha de emisión: ${view.issuedWhen}`) : '',
    meta(`Razón social: ${view.buyerName}`),
    view.buyerCuit ? meta(`CUIT: ${view.buyerCuit}`) : '',
    view.buyerTaxCondition ? meta(`Condición IVA: ${view.buyerTaxCondition}`) : '',
    view.salePaymentCondition ? meta(`Condición de venta: ${view.salePaymentCondition}`) : '',
  ].join('');

  const totalsHtml = [
    view.discriminatesIva && view.netAmount != null
      ? `<div class="row">Neto gravado ${view.netAmount}</div>`
      : '',
    view.discriminatesIva && view.ivaAmount != null
      ? `<div class="row">IVA ${view.ivaAmount}</div>`
      : '',
    !view.discriminatesIva
      ? `<div class="row">${escapeReceiptHtml(fiscalIvaIncludedLegend(view.emitterTaxCondition))}</div>`
      : '',
    `<div class="total">TOTAL ${view.total}</div>`,
  ].join('');

  const qrHtml = view.qrImageDataUrl
    ? `<div class="center"><img src="${escapeReceiptHtml(view.qrImageDataUrl)}" alt="QR AFIP" style="display:block;width:48mm;max-width:100%;margin:6px auto;"/></div>`
    : view.qrUrl
      ? '<div class="meta center">QR no disponible</div>'
      : '';

  const body = `
  ${emitterHtml}
  <div class="title">${escapeReceiptHtml(view.docTypeLabel)}</div>
  ${view.copyLabel ? `<div class="meta center">${escapeReceiptHtml(view.copyLabel)}</div>` : ''}
  <hr class="sep"/>
  ${buyerHtml}
  <hr class="sep"/>
  <table>${thermalItemRowsHtml(payload.lines) || '<tr><td>(sin ítems)</td></tr>'}</table>
  <hr class="sep"/>
  ${totalsHtml}
  <hr class="sep"/>
  ${view.relatedDocLabel ? meta(view.relatedDocLabel, 'meta center') : ''}
  ${view.qrUrl ? '<div class="foot">Escanee el código para verificar en AFIP</div>' : ''}
  ${qrHtml}
  <div class="foot">${escapeReceiptHtml(`C.A.E. N° ${view.cae}`)}</div>
  ${view.caeExpiration ? `<div class="foot">Fecha de vto. CAE: ${escapeReceiptHtml(view.caeExpiration)}</div>` : ''}
  <div class="foot">Comprobante autorizado por AFIP</div>
  ${view.saleRef ? `<div class="foot">Referencia interna: ${escapeReceiptHtml(view.saleRef)}</div>` : ''}
  ${
    view.isHomologacion
      ? '<div class="void-banner">COMPROBANTE DE PRUEBA — SIN VALIDEZ FISCAL</div>'
      : ''
  }`;

  const tracking = pickupTrackingUrl(payload.trackingUrl);
  const pickupHtml =
    tracking != null
      ? `<hr class="sep"/><div class="foot center">${escapeReceiptHtml(
          pickupTrackingQrLabel(payload.fulfillmentLabel),
        )}</div><div class="center">${pickupTrackingQrHtml(
          tracking,
          pickupTrackingQrLabel(payload.fulfillmentLabel),
        )}</div>`
      : '';

  return {
    text,
    html: wrapThermalHtml(body + pickupHtml, widthMm),
    escpos: renderFiscalComprobanteEscPos(payload),
  };
}
